import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack, withShade} from '@dropbox/dig-foundations';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  type TableOfContentsEntry,
  TableOfContentsPlugin as LexicalTableOfContentsPlugin,
} from '@lexical/react/LexicalTableOfContentsPlugin';
import {useCollabId} from 'components/DSYS/CollabProvider';
import type {LexicalEditor} from 'lexical';
import {expandCollapsedSectionsForHeading} from 'plugins/enhancedHeadings/headingCollapseNavigation';
import {useCallback, useEffect, useState} from 'react';

import styles from './TableOfContentsPlugin.module.css';

const TOC_SCROLL_OFFSET_PX = 72;

const scrollToTocHeading = (editor: LexicalEditor, key: string) => {
  const element = editor.getElementByKey(key);
  if (!element) return;

  const y = element.getBoundingClientRect().top + window.scrollY - TOC_SCROLL_OFFSET_PX;
  window.scrollTo({top: y, behavior: 'smooth'});
};

const TableOfContentsContent = ({
  entries,
  activeKey,
  onEntriesChange,
  isOpen,
}: {
  entries: TableOfContentsEntry[];
  activeKey: string | null;
  onEntriesChange: (entries: TableOfContentsEntry[]) => void;
  isOpen: boolean;
}) => {
  const [editor] = useLexicalComposerContext();
  const collabId = useCollabId();

  const handleTocEntryClick = useCallback(
    (key: string) => {
      const dom = editor.getElementByKey(key);
      if (!dom) return;

      const didExpand = expandCollapsedSectionsForHeading({
        editor,
        headingElement: dom,
        collabId,
      });

      if (didExpand) {
        window.setTimeout(() => {
          requestAnimationFrame(() => scrollToTocHeading(editor, key));
        }, 0);
        return;
      }

      scrollToTocHeading(editor, key);
    },
    [collabId, editor]
  );

  // Update parent component when entries change
  const entriesJSON = JSON.stringify(entries);
  useEffect(() => {
    if (entries?.length) {
      onEntriesChange(entries);
    }
    // Ignore the missing `effects` dep, because entriesJSON will take care of
    // any practical change that would affect the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entriesJSON, onEntriesChange]);

  const items = entries.filter(([, , tag]) => tag === 'h1' || tag === 'h2');

  return (
    <>
      {/* Lines view */}
      <Box
        position="absolute"
        paddingRight="8"
        paddingTop="28"
        visibility={!isOpen ? 'visible' : 'hidden'}
        style={{
          opacity: isOpen ? 0 : 0.8,
          transition: 'transform 0.2s ease-in-out, opacity 0.7s',
          transform: !isOpen ? 'translateX(0)' : 'translateX(10px)',
        }}
      >
        {items.map(([key, , tag]) => {
          const isActive = key === activeKey;
          return (
            <Box
              key={key}
              borderWidth="2"
              borderBottom="Solid"
              borderColor={isActive ? 'Primary Base' : 'Border Base'}
              style={{
                width: tag === 'h1' ? 28 : 14,
                height: 27,
                marginBottom: 4,
              }}
            />
          );
        })}
      </Box>

      {/* Text view - visible on hover */}
      <Box
        position="absolute"
        padding="12"
        boxShadow="Raised"
        borderRadius="Medium"
        backgroundColor="Background Raised"
        visibility={isOpen ? 'visible' : 'hidden'}
        opacity={isOpen ? 1 : 0}
        paddingLeft="32"
        style={{
          transform: isOpen ? 'translateX(-32px)' : 'translateX(-200px)',
          transition: 'transform 0.2s ease-in-out, opacity 0.7s',
          height: 'auto',
          minHeight: 'fit-content',
        }}
        width="max-content"
      >
        <Stack>
          {items.map(([key, text, tag]) => {
            // Check if this Lexical key matches our active key
            const isActive = key === activeKey;
            return (
              <Box
                paddingY="4"
                cursor="pointer"
                paddingLeft={tag === 'h2' ? '32' : '8'}
                paddingRight="8"
                borderRadius="Medium"
                key={key}
                onClick={() => handleTocEntryClick(key)}
                {...withShade({style: {minHeight: 20}})}
              >
                <Text isBold={isActive} color={isActive ? undefined : 'subtle'}>
                  <Truncate>{text}</Truncate>
                </Text>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </>
  );
};

export const TableOfContentsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [currentEntries, setCurrentEntries] = useState<TableOfContentsEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const handleScroll = useCallback(
    (lexicalEntries: TableOfContentsEntry[]) => {
      if (!lexicalEntries.length) return;

      const scrollTop = window.scrollY;
      const offset = 100; // Offset to trigger section change

      // Find the current section based on scroll position
      let currentKey = null;

      for (let i = lexicalEntries.length - 1; i >= 0; i--) {
        const [key] = lexicalEntries[i];
        const element = editor.getElementByKey(key);

        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + scrollTop;

          if (scrollTop + offset >= elementTop) {
            currentKey = key;
            break;
          }
        }
      }

      // If we're at the very top, activate the first heading
      if (scrollTop < offset && lexicalEntries.length > 0) {
        currentKey = lexicalEntries[0][0];
      }

      setActiveKey(currentKey);
    },
    [editor]
  );

  useEffect(() => {
    let rafId: number;

    const scrollHandler = () => {
      if (rafId) return; // Skip if already scheduled

      rafId = requestAnimationFrame(() => {
        if (currentEntries.length > 0) {
          handleScroll(currentEntries);
        }
        rafId = 0;
      });
    };

    // Add scroll listener
    window.addEventListener('scroll', scrollHandler, {passive: true});

    // Initial call
    scrollHandler();

    return () => {
      window.removeEventListener('scroll', scrollHandler);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [currentEntries, handleScroll]);

  return (
    <Box
      className={styles.toc}
      position="fixed"
      overflow="visible"
      paddingX="12"
      paddingY="8"
      backgroundColor="Background Base"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      style={{height: 'auto', minHeight: 'fit-content'}}
    >
      <LexicalTableOfContentsPlugin>
        {(entries: TableOfContentsEntry[]) => (
          <TableOfContentsContent
            entries={entries.filter(([, , tag]) => tag === 'h1' || tag === 'h2')}
            activeKey={activeKey}
            onEntriesChange={setCurrentEntries}
            isOpen={isOpen}
          />
        )}
      </LexicalTableOfContentsPlugin>
    </Box>
  );
};
